import { useEffect, useRef, useState } from 'react'
import { Check, ChevronDown, Plus, X, Mail, Github, Server, Figma, BookOpen, Shield } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { FormField } from '@/components/ui/form'
import { Modal } from '@/components/ui/modal'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { Select } from '@/components/ui/select'
import { personFromName, type Person } from '@/components/tickets/data'
import { api } from '@/services/api'
import { fetchClientsWithEmails, formatClientLabel, type ClientWithEmails } from '@/services/clients'
import { useAuth } from '@/contexts/AuthContext'
import { cn } from '@/lib/utils'
import { ProjectVisibility, ProjectVisibilityLabels, enumToSelectOptions } from '@/utils/enum'

export interface User {
  id: string
  nom: string
  email: string
  role: string
}

export interface ActivitySector {
  id: number
  nom: string
}

export interface ProjectFormData {
  clientId?: string
  clientName?: string
  clientEmail?: string
  clientEmails?: string[]  // Multiple emails for new client
  clientNom?: string  // Store client name when selecting existing client
  clientEmailExisting?: string  // Store client email when selecting existing client
  projectManagerId?: string  // Chef de projet principal (= premier de projectManagerIds)
  projectManagerIds?: string[]
  projectManagerName?: string  // For creating new PM
  projectManagerEmail?: string  // For creating new PM
  nomProjet: string
  dateKickoff: string
  description: string
  descriptionInterne?: string
  stackTechnique: string
  developpeurs: string
  developerIds?: string[]
  urlGithub: string
  urlServeur: string
  urlFigma?: string
  // Multiple URLs support
  urlsGithub?: string[]
  urlsServeur?: string[]
  urlsFigma?: string[]
  urlsNotion?: string[]
  activitySectorId?: string
  newActivitySectorName?: string  // For creating new sector
  /** externe (défaut) | interne — super admin */
  visibility?: string
}

export interface CreateProjectModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: ProjectFormData) => void | Promise<void>
  isLoading?: boolean
}

export function CreateProjectModal({
  open,
  onOpenChange,
  onSubmit,
  isLoading = false,
}: CreateProjectModalProps) {
  const { user } = useAuth()
  const isSuperAdmin = user?.email?.endsWith('@qolaig.com') || user?.email?.endsWith('@qolaig.fr')
  
  const [clientType, setClientType] = useState<'existing' | 'new'>('existing')
  const [pmType, setPmType] = useState<'existing' | 'new'>('existing')
  const [showNewSectorInput, setShowNewSectorInput] = useState(false)
  const [clients, setClients] = useState<ClientWithEmails[]>([])
  const [people, setPeople] = useState<Person[]>([])
  const [developerIds, setDeveloperIds] = useState<string[]>([])
  const [activitySectors, setActivitySectors] = useState<ActivitySector[]>([])
  const [loadingClients, setLoadingClients] = useState(false)
  const [loadingPeople, setLoadingPeople] = useState(false)
  const [loadingSectors, setLoadingSectors] = useState(false)
  const [creatingSector, setCreatingSector] = useState(false)

  const [formData, setFormData] = useState<ProjectFormData>({
    clientId: '',
    clientName: '',
    clientEmail: '',
    clientEmails: [''],
    clientNom: '',
    clientEmailExisting: '',
    projectManagerId: '',
    projectManagerIds: [],
    projectManagerName: '',
    projectManagerEmail: '',
    nomProjet: '',
    dateKickoff: '',
    description: '',
    descriptionInterne: '',
    stackTechnique: '',
    developpeurs: '',
    urlGithub: '',
    urlServeur: '',
    urlFigma: '',
    urlsGithub: [''],
    urlsServeur: [''],
    urlsFigma: [''],
    urlsNotion: [''],
    activitySectorId: '',
    newActivitySectorName: '',
    visibility: ProjectVisibility.EXTERNE,
  })

  // Fetch clients, project managers and activity sectors when modal opens
  useEffect(() => {
    if (open) {
      fetchClients()
      fetchPeople()
      fetchActivitySectors()
    }
  }, [open])

  const fetchClients = async () => {
    setLoadingClients(true)
    try {
      setClients(await fetchClientsWithEmails())
    } catch (error) {
      console.error('Erreur lors de la récupération des clients:', error)
      setClients([])
    } finally {
      setLoadingClients(false)
    }
  }

  const fetchPeople = async () => {
    setLoadingPeople(true)
    try {
      const response = await api.get<{ data: User[]; count: number }>('/users', { internal: 'true' })
      const users = response.data.data || []
      const next = users
        .filter((user) => user.role === 'admin' || user.role === 'super_admin')
        .map((user) => personFromName(user.id, user.nom))
        .sort((a, b) => a.name.localeCompare(b.name, 'fr'))
      setPeople(next)
    } catch (error) {
      console.error('Erreur lors de la récupération des développeurs:', error)
      setPeople([])
    } finally {
      setLoadingPeople(false)
    }
  }

  const toggleDeveloper = (id: string) => {
    setDeveloperIds((current) => {
      const next = current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
      const names = next
        .map((personId) => people.find((person) => person.id === personId)?.name)
        .filter((name): name is string => Boolean(name))
      setFormData((prev) => ({ ...prev, developpeurs: names.join(', ') }))
      return next
    })
  }

  const fetchActivitySectors = async () => {
    setLoadingSectors(true)
    try {
      const response = await api.get<{ data: ActivitySector[]; count: number }>('/activity-sectors')
      const sectors = response.data.data || []
      setActivitySectors(sectors)
    } catch (error) {
      console.error('Erreur lors de la récupération des secteurs d\'activité:', error)
      setActivitySectors([])
    } finally {
      setLoadingSectors(false)
    }
  }

  const handleCreateSector = async () => {
    if (!formData.newActivitySectorName?.trim()) {
      alert('Veuillez entrer un nom pour le secteur d\'activité')
      return
    }

    setCreatingSector(true)
    try {
      const response = await api.post<ActivitySector>('/activity-sectors', {
        nom: formData.newActivitySectorName.trim()
      })
      const newSector = response.data
      
      // Add to list and select it
      setActivitySectors(prev => [...prev, newSector])
      setFormData(prev => ({
        ...prev,
        activitySectorId: String(newSector.id),
        newActivitySectorName: ''
      }))
      setShowNewSectorInput(false)
    } catch (error: any) {
      console.error('Erreur lors de la création du secteur:', error)
      const errorMessage = error?.message || error?.response?.data?.detail || 'Erreur lors de la création du secteur'
      alert(errorMessage)
    } finally {
      setCreatingSector(false)
    }
  }

  const handleInputChange = (field: keyof ProjectFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleEmailChange = (index: number, value: string) => {
    const newEmails = [...(formData.clientEmails || [''])]
    newEmails[index] = value
    setFormData((prev) => ({ ...prev, clientEmails: newEmails }))
  }

  const addEmailField = () => {
    setFormData((prev) => ({
      ...prev,
      clientEmails: [...(prev.clientEmails || ['']), '']
    }))
  }

  const removeEmailField = (index: number) => {
    const newEmails = [...(formData.clientEmails || [''])]
    if (newEmails.length > 1) {
      newEmails.splice(index, 1)
      setFormData((prev) => ({ ...prev, clientEmails: newEmails }))
    }
  }

  // URL management functions
  const handleUrlChange = (urlType: 'urlsGithub' | 'urlsServeur' | 'urlsFigma' | 'urlsNotion', index: number, value: string) => {
    const newUrls = [...(formData[urlType] || [''])]
    newUrls[index] = value
    setFormData((prev) => ({ ...prev, [urlType]: newUrls }))
  }

  const addUrlField = (urlType: 'urlsGithub' | 'urlsServeur' | 'urlsFigma' | 'urlsNotion') => {
    setFormData((prev) => ({
      ...prev,
      [urlType]: [...(prev[urlType] || ['']), '']
    }))
  }

  const removeUrlField = (urlType: 'urlsGithub' | 'urlsServeur' | 'urlsFigma' | 'urlsNotion', index: number) => {
    const newUrls = [...(formData[urlType] || [''])]
    if (newUrls.length > 1) {
      newUrls.splice(index, 1)
      setFormData((prev) => ({ ...prev, [urlType]: newUrls }))
    }
  }

  const handleSubmit = async () => {
    // For existing client, use all emails from clientEmails if available
    // For new client, filter out empty emails
    let validEmails: string[] = []
    if (formData.clientId && formData.clientEmails && formData.clientEmails.length > 0) {
      // Existing client - use all stored emails
      validEmails = formData.clientEmails.filter(e => e && e.trim())
    } else {
      // New client - filter out empty emails
      validEmails = (formData.clientEmails || []).filter(e => e && e.trim())
    }
    
    const dataToSubmit = {
      ...formData,
      developerIds,
      clientEmails: validEmails.length > 0 ? validEmails : undefined,
      clientEmail: validEmails[0] || formData.clientEmail || ''
    }
    await onSubmit(dataToSubmit)
    // Reset form after successful submission
    resetForm()
  }

  const resetForm = () => {
    setFormData({
      clientId: '',
      clientName: '',
      clientEmail: '',
      clientEmails: [''],
      clientNom: '',
      clientEmailExisting: '',
      projectManagerId: '',
      projectManagerIds: [],
      projectManagerName: '',
      projectManagerEmail: '',
      nomProjet: '',
      dateKickoff: '',
      description: '',
      descriptionInterne: '',
      stackTechnique: '',
      developpeurs: '',
      urlGithub: '',
      urlServeur: '',
      urlFigma: '',
      urlsGithub: [''],
      urlsServeur: [''],
      urlsFigma: [''],
      urlsNotion: [''],
      activitySectorId: '',
      newActivitySectorName: '',
      visibility: ProjectVisibility.EXTERNE,
    })
    setDeveloperIds([])
    setClientType('existing')
    setPmType('existing')
    setShowNewSectorInput(false)
  }

  const handleCancel = () => {
    onOpenChange(false)
    resetForm()
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      hideHeader={true}
      contentClassName="border-4 border-cyan-400 rounded-2xl shadow-2xl"
      footer={
        <div className="flex gap-3 pt-4 justify-start">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            isLoading={isLoading}
            className="bg-gradient-to-r from-cyan-400 to-cyan-500 text-white hover:from-cyan-500 hover:to-cyan-600 shadow-md"
          >
            Créer le projet
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <div className="bg-white border-b border-gray-200 px-6 py-4 -mx-6 -mt-6 mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Créer un nouveau projet</h2>
            <p className="text-sm text-gray-500 mt-1">
              Ajoutez un nouveau projet pour un client existant ou créez un nouveau client
            </p>
          </div>
        </div>

        {/* Client Section */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-3">Client</label>
          <div className="flex gap-3 mb-3">
            <Button
              type="button"
              onClick={() => setClientType('existing')}
              variant="outline"
              className={`flex-1 ${
                clientType === 'existing'
                  ? 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300'
                  : 'bg-white text-gray-600 hover:bg-gray-50 border-gray-200'
              }`}
            >
              Client existant
            </Button>
            <Button
              type="button"
              onClick={() => setClientType('new')}
              variant="outline"
              className={`flex-1 ${
                clientType === 'new'
                  ? 'bg-gradient-to-r from-cyan-400 to-cyan-500 text-white shadow-md border-transparent hover:from-cyan-500 hover:to-cyan-600'
                  : 'bg-white text-gray-600 hover:bg-gray-50 border-gray-200'
              }`}
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouveau client
            </Button>
          </div>

          {clientType === 'new' ? (
            <div className="space-y-4 border border-blue-200 rounded-lg p-4 bg-blue-50/30">
              <FormField label="Nom du nouveau client" required>
                <Input
                  type="text"
                  value={formData.clientName || ''}
                  onChange={(e) => handleInputChange('clientName', e.target.value)}
                  placeholder="Ex: ACME Corporation"
                  className="bg-white border-gray-200 focus:ring-cyan-400 focus:border-transparent"
                />
              </FormField>
              <FormField label="Emails du client" required>
                <div className="space-y-2">
                  {(formData.clientEmails || ['']).map((email, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          type="email"
                          value={email}
                          onChange={(e) => handleEmailChange(index, e.target.value)}
                          placeholder="contact@exemple.com"
                          className="bg-white border-gray-200 focus:ring-cyan-400 focus:border-transparent pl-10"
                        />
                      </div>
                      {(formData.clientEmails?.length || 0) > 1 && (
                        <button
                          type="button"
                          onClick={() => removeEmailField(index)}
                          className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title="Supprimer cet email"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addEmailField}
                    className="flex items-center gap-2 text-sm text-cyan-600 hover:text-cyan-700 mt-2"
                  >
                    <Plus className="w-4 h-4" />
                    Ajouter un email
                  </button>
                  <p className="text-xs text-gray-500 mt-1">
                    💡 Chaque email pourra se connecter à la plateforme et accéder au projet.
                  </p>
                </div>
              </FormField>
            </div>
          ) : (
            <SearchableSelect
              value={formData.clientId || ''}
              onChange={(value) => {
                const selectedClient = clients.find(c => c.id === value)
                if (!selectedClient) return
                const allEmails = selectedClient.emails.length > 0
                  ? selectedClient.emails
                  : (selectedClient.email ? [selectedClient.email] : [])
                setFormData(prev => ({
                  ...prev,
                  clientId: value,
                  clientNom: selectedClient.nom,
                  clientEmailExisting: allEmails[0] || '',
                  clientEmails: allEmails,
                }))
              }}
              options={clients.map(client => ({
                value: client.id,
                label: formatClientLabel(client.nom, client.emails)
              }))}
              placeholder={loadingClients ? "Chargement..." : clients.length === 0 ? "Aucun client disponible" : "Sélectionner un client"}
              searchPlaceholder="Rechercher un client..."
              disabled={loadingClients || clients.length === 0}
              className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent"
            />
          )}
        </div>

        {/* Activity Sector Section */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-3">Secteur d'activité</label>
          
          {showNewSectorInput ? (
            <div className="space-y-4 border border-blue-200 rounded-lg p-4 bg-blue-50/30">
              <FormField label="Nom du nouveau secteur" required>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={formData.newActivitySectorName || ''}
                    onChange={(e) => handleInputChange('newActivitySectorName', e.target.value)}
                    placeholder="Ex: E-commerce, Fintech, Santé..."
                    className="bg-white border-gray-200 focus:ring-cyan-400 focus:border-transparent flex-1"
                  />
                  <Button
                    type="button"
                    onClick={handleCreateSector}
                    isLoading={creatingSector}
                    disabled={!formData.newActivitySectorName?.trim() || creatingSector}
                    className="bg-gradient-to-r from-cyan-400 to-cyan-500 text-white hover:from-cyan-500 hover:to-cyan-600 shadow-md"
                  >
                    Créer
                  </Button>
                </div>
              </FormField>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowNewSectorInput(false)}
                className="text-sm"
              >
                Annuler
              </Button>
            </div>
          ) : (
            <SearchableSelect
              value={formData.activitySectorId || ''}
              onChange={(value) => handleInputChange('activitySectorId', value)}
              options={activitySectors.map(sector => ({
                value: String(sector.id),
                label: sector.nom
              }))}
              placeholder={loadingSectors ? "Chargement..." : "Sélectionner un secteur d'activité"}
              searchPlaceholder="Rechercher un secteur..."
              disabled={loadingSectors}
              className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent"
              showAddButton={true}
              addButtonLabel="Ajouter un secteur"
              onAddClick={() => setShowNewSectorInput(true)}
            />
          )}
        </div>

        {/* Project Manager Section */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-3">Chef de projet</label>
          <div className="flex gap-3 mb-3">
            <Button
              type="button"
              onClick={() => setPmType('existing')}
              variant="outline"
              className={`flex-1 ${
                pmType === 'existing'
                  ? 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300'
                  : 'bg-white text-gray-600 hover:bg-gray-50 border-gray-200'
              }`}
            >
              Chef de projet existant
            </Button>
            <Button
              type="button"
              onClick={() => setPmType('new')}
              variant="outline"
              className={`flex-1 ${
                pmType === 'new'
                  ? 'bg-gradient-to-r from-cyan-400 to-cyan-500 text-white shadow-md border-transparent hover:from-cyan-500 hover:to-cyan-600'
                  : 'bg-white text-gray-600 hover:bg-gray-50 border-gray-200'
              }`}
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouveau chef de projet
            </Button>
          </div>

          {pmType === 'new' ? (
            <div className="space-y-4 border border-blue-200 rounded-lg p-4 bg-blue-50/30">
              <FormField label="Nom du chef de projet" required>
                <Input
                  type="text"
                  value={formData.projectManagerName || ''}
                  onChange={(e) => handleInputChange('projectManagerName', e.target.value)}
                  placeholder="Ex: Jean Dupont"
                  className="bg-white border-gray-200 focus:ring-cyan-400 focus:border-transparent"
                />
              </FormField>
              <FormField label="Email du chef de projet" required>
                <Input
                  type="email"
                  value={formData.projectManagerEmail || ''}
                  onChange={(e) => handleInputChange('projectManagerEmail', e.target.value)}
                  placeholder="jean.dupont@qolaig.com"
                  className="bg-white border-gray-200 focus:ring-cyan-400 focus:border-transparent"
                />
              </FormField>
              <p className="text-xs text-gray-500">
                💡 Le chef de projet pourra se connecter à la plateforme.
              </p>
            </div>
          ) : (
            <PeopleSelect
              people={people}
              selectedIds={formData.projectManagerIds ?? []}
              onToggle={(id) =>
                setFormData((prev) => {
                  const current = prev.projectManagerIds ?? []
                  const next = current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
                  return { ...prev, projectManagerIds: next, projectManagerId: next[0] ?? '' }
                })
              }
              loading={loadingPeople}
              loadingLabel="Chargement des chefs de projet…"
              placeholder="Sélectionner un ou plusieurs chefs de projet"
              searchPlaceholder="Rechercher un chef de projet..."
              emptyLabel="Aucun chef de projet"
            />
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Nom du projet" required>
            <Input
              type="text"
              value={formData.nomProjet}
              onChange={(e) => handleInputChange('nomProjet', e.target.value)}
              placeholder="Ex: Plateforme IA Analytics"
              className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent"
            />
          </FormField>

          <FormField label="Date de kickoff" required>
            <Input
              type="date"
              value={formData.dateKickoff}
              onChange={(e) => handleInputChange('dateKickoff', e.target.value)}
              className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent"
            />
          </FormField>
        </div>

        <FormField label="Description" required>
          <Textarea
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Description du projet visible par le client"
            rows={4}
            className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent resize-none"
          />
        </FormField>

        {/* Description interne - visible uniquement pour les super admins */}
        {isSuperAdmin && (
          <FormField label="Type de projet">
            <Select
              value={formData.visibility || ProjectVisibility.EXTERNE}
              onChange={(e) => handleInputChange('visibility', e.target.value)}
              options={enumToSelectOptions(ProjectVisibility, ProjectVisibilityLabels)}
              className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent"
            />
          </FormField>
        )}

        {isSuperAdmin && (
          <FormField label="Description interne (Super Admin uniquement)">
            <div className="space-y-2">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4 text-cyan-500" />
                <span className="text-xs text-gray-500">Visible uniquement par les super admins</span>
              </div>
              <Textarea
                value={formData.descriptionInterne || ''}
                onChange={(e) => handleInputChange('descriptionInterne', e.target.value)}
                placeholder="Description interne du projet (non visible par le client)"
                rows={4}
                className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent resize-none"
              />
            </div>
          </FormField>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Stack technique (séparés par virgule)">
            <Input
              type="text"
              value={formData.stackTechnique}
              onChange={(e) => handleInputChange('stackTechnique', e.target.value)}
              placeholder="React, TypeScript, Supabase"
              className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent"
            />
          </FormField>

          <FormField label="Développeurs">
            <PeopleSelect
              people={people}
              selectedIds={developerIds}
              onToggle={toggleDeveloper}
              loading={loadingPeople}
              loadingLabel="Chargement des développeurs…"
              placeholder="Sélectionner des développeurs"
              searchPlaceholder="Rechercher un développeur..."
              emptyLabel="Aucun développeur"
            />
          </FormField>
        </div>

        <FormField label="URLs GitHub (optionnel)">
          <div className="space-y-2">
            {(formData.urlsGithub || ['']).map((url, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Github className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="url"
                    value={url}
                    onChange={(e) => handleUrlChange('urlsGithub', index, e.target.value)}
                    placeholder="https://github.com/..."
                    className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent pl-10"
                  />
                </div>
                {(formData.urlsGithub?.length || 0) > 1 && (
                  <button
                    type="button"
                    onClick={() => removeUrlField('urlsGithub', index)}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={() => addUrlField('urlsGithub')}
              className="flex items-center gap-2 text-sm text-cyan-600 hover:text-cyan-700"
            >
              <Plus className="w-4 h-4" />
              Ajouter une URL GitHub
            </button>
          </div>
        </FormField>

        <FormField label="URLs Serveur (optionnel)">
          <div className="space-y-2">
            {(formData.urlsServeur || ['']).map((url, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Server className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="url"
                    value={url}
                    onChange={(e) => handleUrlChange('urlsServeur', index, e.target.value)}
                    placeholder="https://app.exemple.com"
                    className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent pl-10"
                  />
                </div>
                {(formData.urlsServeur?.length || 0) > 1 && (
                  <button
                    type="button"
                    onClick={() => removeUrlField('urlsServeur', index)}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={() => addUrlField('urlsServeur')}
              className="flex items-center gap-2 text-sm text-cyan-600 hover:text-cyan-700"
            >
              <Plus className="w-4 h-4" />
              Ajouter une URL Serveur
            </button>
          </div>
        </FormField>

        <FormField label="URLs Figma (optionnel)">
          <div className="space-y-2">
            {(formData.urlsFigma || ['']).map((url, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Figma className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="url"
                    value={url}
                    onChange={(e) => handleUrlChange('urlsFigma', index, e.target.value)}
                    placeholder="https://figma.com/..."
                    className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent pl-10"
                  />
                </div>
                {(formData.urlsFigma?.length || 0) > 1 && (
                  <button
                    type="button"
                    onClick={() => removeUrlField('urlsFigma', index)}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={() => addUrlField('urlsFigma')}
              className="flex items-center gap-2 text-sm text-cyan-600 hover:text-cyan-700"
            >
              <Plus className="w-4 h-4" />
              Ajouter une URL Figma
            </button>
          </div>
        </FormField>

        <FormField label="URLs Notion (optionnel)">
          <div className="space-y-2">
            {(formData.urlsNotion || ['']).map((url, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="relative flex-1">
                  <BookOpen className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="url"
                    value={url}
                    onChange={(e) => handleUrlChange('urlsNotion', index, e.target.value)}
                    placeholder="https://www.notion.so/..."
                    className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent pl-10"
                  />
                </div>
                {(formData.urlsNotion?.length || 0) > 1 && (
                  <button
                    type="button"
                    onClick={() => removeUrlField('urlsNotion', index)}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={() => addUrlField('urlsNotion')}
              className="flex items-center gap-2 text-sm text-cyan-600 hover:text-cyan-700"
            >
              <Plus className="w-4 h-4" />
              Ajouter une URL Notion
            </button>
          </div>
        </FormField>
      </div>
    </Modal>
  )
}

export function PeopleSelect({
  people,
  selectedIds,
  onToggle,
  loading,
  multiple = true,
  loadingLabel,
  placeholder,
  searchPlaceholder,
  emptyLabel,
}: {
  people: Person[]
  selectedIds: string[]
  onToggle: (id: string) => void
  loading: boolean
  multiple?: boolean
  loadingLabel: string
  placeholder: string
  searchPlaceholder: string
  emptyLabel: string
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const rootRef = useRef<HTMLDivElement>(null)
  const selected = people.filter((person) => selectedIds.includes(person.id))
  const visible = people.filter((person) => person.name.toLowerCase().includes(search.trim().toLowerCase()))
  const label = loading
    ? loadingLabel
    : selected.length === 0
      ? placeholder
      : selected.map((person) => person.name).join(', ')

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false)
        setSearch('')
      }
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
        setSearch('')
      }
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        disabled={loading}
        onClick={() => setOpen((current) => !current)}
        className="flex h-10 w-full items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 text-left text-sm disabled:opacity-60"
      >
        <span className={cn('truncate', selected.length === 0 && 'text-gray-400')}>{label}</span>
        <ChevronDown className={cn('h-4 w-4 shrink-0 text-gray-400', open && 'rotate-180')} />
      </button>
      {open ? (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
          <div className="border-b border-gray-100 p-2">
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={searchPlaceholder}
              className="h-9 w-full rounded-md border border-gray-200 bg-gray-50 px-3 text-sm outline-none focus:ring-2 focus:ring-cyan-400"
            />
          </div>
          <div className="max-h-60 overflow-auto py-1">
            {visible.length === 0 ? (
              <p className="px-3 py-2 text-center text-sm text-gray-400">{emptyLabel}</p>
            ) : (
              visible.map((person) => {
                const checked = selectedIds.includes(person.id)
                return (
                  <button
                    key={person.id}
                    type="button"
                    onClick={() => {
                      onToggle(person.id)
                      if (!multiple) {
                        setOpen(false)
                        setSearch('')
                      }
                    }}
                    className={cn(
                      'flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50',
                      checked && 'bg-[#f3fbf9]'
                    )}
                  >
                    <span
                      className="flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold text-white"
                      style={{ backgroundColor: person.color }}
                    >
                      {person.initial}
                    </span>
                    <span className="truncate">{person.name}</span>
                    {checked ? <Check className="ml-auto h-4 w-4 text-[#14c4b0]" /> : null}
                  </button>
                )
              })
            )}
          </div>
        </div>
      ) : null}
    </div>
  )
}
